/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';

const Server = () => (<></>);

/**
 * Wraps a dynamic import in a component that loads the module on its first mount and renders one
 * of its exports. A failed import is tried again. See .claude/context/code-splitting.md.
 *
 * @param {() => Promise<Object>} importer  the module's `import()`, as a thunk at the call site,
 *   so Rollup can still split the chunk. Example: `() => import('./Foo')`.
 * @param {string} exportName  the export of the module to render.
 * @param {Object} [options]
 * @param {string} [options.label=exportName]  the name in the load error and in `displayName`.
 * @param {import('react').ReactNode} [options.placeholder=null]  what renders while the module
 *   loads, and after a failed load when `rethrowOnError` is false.
 * @param {string} [options.errorBoundaryName]  wraps the loaded component in an `ErrorBoundary`
 *   with this name.
 * @param {boolean} [options.rethrowOnError=false]  throws a failed load during render, so a parent
 *   error boundary catches it.
 * @param {number} [options.retries=3]  how many more times a failed import is tried.
 * @returns the component, which also has `preload()`. On the server it renders nothing.
 */
export const createLazyComponent = (importer, exportName, {
  label = exportName,
  placeholder = null,
  errorBoundaryName = null,
  rethrowOnError = false,
  retries = 3,
} = {}) => {
  const retryingImport = (remaining = retries) => importer().catch((err) => {
    if (remaining > 0) return retryingImport(remaining - 1);
    throw err;
  });

  // Shared by every instance: once loaded (or preloaded), a mount renders the component on its
  // first pass instead of showing the placeholder again.
  let loadedComponent = null;
  const load = () => retryingImport().then((module) => {
    if (!module[exportName]) throw new Error(`Error importing ${label}.`);
    loadedComponent = module[exportName];
    return loadedComponent;
  });

  const LazyComponent = (props) => {
    const moduleRef = useRef(loadedComponent);
    const [, forceRender] = useState();
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
      if (moduleRef.current) return undefined;
      let active = true;
      load()
        .then((component) => {
          if (!active) return;
          moduleRef.current = component;
          forceRender(new Date().getTime());
        })
        .catch((err) => { if (active) setLoadError(err); });
      return () => { active = false; };
    }, []);

    if (rethrowOnError && loadError) throw loadError;
    if (typeof window === 'undefined') return Server();
    if (!moduleRef.current) return placeholder ?? Server();

    const element = (<moduleRef.current {...props} />);
    return errorBoundaryName
      ? <ErrorBoundary name={errorBoundaryName}>{element}</ErrorBoundary>
      : element;
  };

  /**
   * Loads the module ahead of the first render, so every mount renders the component on its first
   * pass. Never rejects: after a failed preload, the first render tries the import again.
   * @returns {Promise<void>}
   */
  LazyComponent.preload = () => load().then(() => undefined, () => undefined);
  LazyComponent.displayName = `Lazy(${label})`;
  return LazyComponent;
};
